import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useAuthStore } from '../store/authStore'
import socketService from '../services/socket'
import api from '../services/api'
import toast from 'react-hot-toast'

const NotificationContext = createContext(null)

export function NotificationProvider({ children }) {
  const { user, isAuthenticated } = useAuthStore()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const connectedRef = useRef(false)

  // Fetch existing notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const { data } = await api.get('/communication/notifications')
      setNotifications(data)
      setUnreadCount(data.filter(n => !n.is_read).length)
    } catch (error) {
      // Notifications endpoint may not exist for all roles, that's OK
      console.debug('Could not fetch notifications:', error.message)
    }
  }, [isAuthenticated])

  // Connect socket when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id && !connectedRef.current) {
      connectedRef.current = true
      socketService.connect(user.id)

      // Listen for real-time notifications
      socketService.on('notification', (data) => {
        // Show toast
        const toastType = data.type === 'success' ? toast.success
          : data.type === 'error' ? toast.error
          : toast

        toastType(data.message || data.title, {
          duration: 5000,
          icon: data.type === 'success' ? '✅'
            : data.type === 'warning' ? '⚠️'
            : data.type === 'error' ? '❌'
            : '🔔',
        })

        // Add to list and bump count
        setNotifications(prev => [data, ...prev])
        setUnreadCount(prev => prev + 1)
      })

      socketService.on('new_message', (data) => {
        toast(`New message from ${data.sender_name || 'someone'}`, {
          icon: '💬',
          duration: 4000,
        })
      })

      fetchNotifications()
    }

    return () => {
      if (!isAuthenticated && connectedRef.current) {
        socketService.disconnect()
        connectedRef.current = false
        setNotifications([])
        setUnreadCount(0)
      }
    }
  }, [isAuthenticated, user?.id, fetchNotifications])

  // Disconnect on logout
  useEffect(() => {
    if (!isAuthenticated && connectedRef.current) {
      socketService.disconnect()
      connectedRef.current = false
      setNotifications([])
      setUnreadCount(0)
    }
  }, [isAuthenticated])

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await api.put(`/communication/notifications/${notificationId}/read`)
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      await api.put('/communication/notifications/mark-all-read')
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }, [])

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await api.delete(`/communication/notifications/${notificationId}`)
      setNotifications(prev => {
        const item = prev.find(n => n.id === notificationId)
        if (item && !item.is_read) {
          setUnreadCount(c => Math.max(0, c - 1))
        }
        return prev.filter(n => n.id !== notificationId)
      })
    } catch (error) {
      console.error('Failed to delete notification:', error)
      // Optimistically remove from state anyway if network error
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
    }
  }, [])

  const clearAllNotifications = useCallback(async () => {
    try {
      await api.delete('/communication/notifications')
      setNotifications([])
      setUnreadCount(0)
    } catch (error) {
      console.error('Failed to clear notifications:', error)
      setNotifications([])
      setUnreadCount(0)
    }
  }, [])

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      fetchNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
